import { HStack, Stack } from "@chakra-ui/react";
import { SqlOperator } from "@src/__generated__/graphql";
import { FiltersPanelFilters } from "@src/utils/components/filters/components/filters-panel-filters";
import { FiltersPanelSearch } from "@src/utils/components/filters/components/filters-panel-search";
import { observer } from "mobx-react-lite";
import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
} from "react";
import {
  createAmountFilter,
  createChoiceListFilter,
  createDateRangeFilters,
  createMultiColumnFilter,
  createPaginatedFilters,
  MultiColumnFilterTypeEnum,
  type TableFilter,
} from "../factories";
import { Filters } from "../models";

type ContextType<T> = {
  filters: Filters<T>;
  filterComponents: TableFilter[];
  appliedCount: number;
};

const FiltersPanelContext = createContext<ContextType<unknown>>({
  filterComponents: [],
  filters: new Filters([]),
  appliedCount: 0,
});

type FiltersPanelRootProps<T> = {
  children: ReactNode;
  filters?: Filters<T>;
};

export const FiltersPanelRoot = observer(function FiltersPanelRoot<T>({
  filters = new Filters([]),
  children,
}: FiltersPanelRootProps<T>) {
  useEffect(() => {
    void filters.fetchTemplates();
  }, [filters]);

  const filterComponents = useMemo(
    () => [
      ...createPaginatedFilters(
        filters.visibleFilters.filter(({ isPaginated }) => isPaginated),
      ),
      ...createChoiceListFilter(
        filters.visibleFilters.filter(
          ({ dateRange, date, isMultiColumn, isPaginated, operator }) =>
            !dateRange &&
            !date &&
            !isMultiColumn &&
            !isPaginated &&
            operator !== SqlOperator.Gte &&
            operator !== SqlOperator.Lte,
        ),
      ),
      ...createAmountFilter(
        filters.visibleFilters.filter(
          ({ operator }) =>
            operator === SqlOperator.Gte || operator === SqlOperator.Lte,
        ),
      ),
      ...createDateRangeFilters(
        filters.visibleFilters.filter(
          ({ dateRange, date, isMultiColumn, isPaginated }) =>
            dateRange || (date && !isMultiColumn && !isPaginated),
        ),
      ),
      ...Object.values(MultiColumnFilterTypeEnum).flatMap((columnType) => {
        return createMultiColumnFilter(
          filters.visibleFilters.filter(
            ({ isMultiColumn, multiColumn, isPaginated }) =>
              isMultiColumn && multiColumn?.type === columnType && !isPaginated,
          ),
          columnType,
        );
      }),
    ],
    [filters.visibleFilters],
  );

  const appliedCount = filters.visibleFilters.filter(
    (f) => f.value.length !== 0,
  ).length;

  return (
    <FiltersPanelContext.Provider
      value={{ filters, appliedCount, filterComponents }}
    >
      {children}
    </FiltersPanelContext.Provider>
  );
});

export function useFiltersPanel<T>(): ContextType<T> {
  const context = useContext(FiltersPanelContext) as ContextType<T>;
  if (!context) {
    throw new Error(
      'useFiltersPanel returned is "undefined". Seems you forgot to wrap the components in "<FiltersPanelRoot />"',
    );
  }
  return context;
}

type FiltersPanelProps<T = unknown> = {
  filters?: Filters<T>;
  searchPlaceholder?: string | undefined;
  onSearch: (term: string) => void;
  searchDefaultValue?: string;
};

export const FiltersPanel = observer(function FiltersPanel<T>({
  filters = new Filters([]),
  searchPlaceholder,
  onSearch,
  searchDefaultValue,
}: FiltersPanelProps<T>) {
  return (
    <FiltersPanelRoot filters={filters}>
      <Stack flex="1" spacing="2">
        <HStack justify="space-between" spacing="3">
          <FiltersPanelSearch
            onSearch={onSearch}
            defaultValue={searchDefaultValue}
            searchPlaceholder={searchPlaceholder}
          />
          {filters.visibleFilters.length > 0 && (
            <FiltersPanelFilters menuListZIndex={31} />
          )}
        </HStack>
      </Stack>
    </FiltersPanelRoot>
  );
});
